/**
 * Support tickets API client (PR-1).
 *
 * Backend: backend/app/api/support.py
 * Spec:    docs/specs/support-agent-onboardix-2026q2/spec.md
 */

import { apiFetch } from './core'
import * as outbox from '../utils/supportOutbox'

const BASE = '/support'

function _isOfflineError(err) {
    if (!err) return false
    if (err.name === 'AbortError' || err.name === 'TypeError') return true
    const msg = String(err.message || err)
    return /Failed to fetch|NetworkError|offline|ERR_INTERNET|ECONN/i.test(msg)
}

export const listTickets = ({ status, limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (limit != null) params.set('limit', String(limit))
    if (offset != null) params.set('offset', String(offset))
    const qs = params.toString()
    return apiFetch(`${BASE}/tickets${qs ? `?${qs}` : ''}`)
}

export const getTicket = (ticketId) =>
    apiFetch(`${BASE}/tickets/${ticketId}`)

export const createTicket = async ({ summary, description = '', moduleKey, route, browserMeta } = {}) => {
    const payload = { summary, description, moduleKey, route, browserMeta }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        // Offline shortcut — don't even try the network. Outbox drains on `online`.
        const queued = await outbox.enqueue(payload)
        return { _queued: true, queueLength: queued }
    }
    try {
        return await apiFetch(`${BASE}/tickets`, {
            method: 'POST',
            body: JSON.stringify({
                summary,
                description,
                module_key: moduleKey || null,
                route: route || null,
                browser_meta: browserMeta || null,
            }),
        })
    } catch (err) {
        if (_isOfflineError(err)) {
            const queued = await outbox.enqueue(payload)
            return { _queued: true, queueLength: queued }
        }
        throw err
    }
}

/**
 * Direct (non-queueing) version for outbox drain — bypasses the offline
 * guard so the drain loop can attempt the actual POST and decide based on
 * the response. Throws on transient failure.
 */
export const _createTicketDirect = ({ summary, description = '', moduleKey, route, browserMeta } = {}) =>
    apiFetch(`${BASE}/tickets`, {
        method: 'POST',
        body: JSON.stringify({
            summary,
            description,
            module_key: moduleKey || null,
            route: route || null,
            browser_meta: browserMeta || null,
        }),
    })

export { outbox as _supportOutbox }

/**
 * Conversational turn (C-2 / C-5): send a message + optional inline media, get
 * the agent's decision + reply. Omit ticketId on the first message — the backend
 * creates a ticket from it. AI route → 60s timeout class in core.js (isAiRoute).
 *
 * `media` — array of { kind, mime, dataBase64 } (screenshots / images / short
 * video / audio). The agent analyses them multimodally (reads the error on a
 * screenshot, hears a voice memo). Backend cap: 18 MB total per turn.
 */
export const sendChat = ({ message, ticketId, moduleKey, route, browserMeta, media } = {}) =>
    apiFetch(`${BASE}/chat`, {
        method: 'POST',
        body: JSON.stringify({
            message,
            ticket_id: ticketId || null,
            module_key: moduleKey || null,
            route: route || null,
            browser_meta: browserMeta || null,
            media: Array.isArray(media) && media.length
                ? media.map((m) => ({ kind: m.kind, mime: m.mime, data_base64: m.dataBase64 }))
                : null,
        }),
    })

export const addMessage = (ticketId, body) =>
    apiFetch(`${BASE}/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body }),
    })

export const changeStatus = (ticketId, status) =>
    apiFetch(`${BASE}/tickets/${ticketId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
    })

// R-2: заявитель подтверждает результат по `fixed`-обращению.
// helped=true → «помогло» (verified); helped=false → «не помогло» (переоткрытие).
export const confirmResolution = (ticketId, helped, comment = '') =>
    apiFetch(`${BASE}/tickets/${ticketId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ helped, comment }),
    })

// R-6: база ответов «Как это работает» для заявителей.
export const kbSearch = (q, limit = 8) =>
    apiFetch(`${BASE}/kb/search?q=${encodeURIComponent(q)}&limit=${limit}`)
export const kbList = (limit = 30) =>
    apiFetch(`${BASE}/kb/list?limit=${limit}`)
export const kbHit = (entryId) =>
    apiFetch(`${BASE}/kb/entries/${entryId}/hit`, { method: 'POST' })

/**
 * Admin: manually (re)run the agent on a ticket (PR-4 «Поддержка · Пульт»).
 * action='decide' — re-run the agent decision on the last user message;
 * action='dispatch' — force a doctor-agent draft PR. Rate-limited per ticket.
 */
export const rerunAgent = (ticketId, action = 'decide') =>
    apiFetch(`${BASE}/tickets/${ticketId}/rerun-agent`, {
        method: 'POST',
        body: JSON.stringify({ action }),
    })

/**
 * Admin batch «Обработать все ждущие»: re-run the agent's decide() over the
 * awaiting-human backlog (up to `limit`). Confirmed bugs dispatch to the
 * doctor-agent; the rest are answered / clarified / escalated.
 */
export const processPending = (limit = 25) =>
    apiFetch(`${BASE}/admin/process-pending?limit=${limit}`, { method: 'POST' })

/**
 * Batch-рассылка: персонализированные Telegram-уведомления авторам готовых тикетов.
 * Обращается по имени из Employee.full_name, direct TG + топик «support».
 */
export const notifyResolved = (limit = 100) =>
    apiFetch(`${BASE}/admin/notify-resolved?limit=${limit}`, { method: 'POST' })

/**
 * Admin: force-process all in_progress/clarifying stuck tickets.
 * feature→planned, bug→dispatch, null→re-triage, how_to→re-decide.
 */
export const forceProcessStuck = (limit = 50) =>
    apiFetch(`${BASE}/admin/force-process-stuck?limit=${limit}`, { method: 'POST' })

// Лента Observability Autopilot: PR/issue с меткой `autopilot` + класс риска.
export const getAutopilotActivity = (limit = 25) =>
    apiFetch(`${BASE}/admin/autopilot-activity?limit=${limit}`)

export const uploadAttachment = async (ticketId, file) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiFetch(`${BASE}/tickets/${ticketId}/attachments`, {
        method: 'POST',
        body: fd,
    })
}

/**
 * Upload a screenshot Blob (from html2canvas + auto-mask + blur brush).
 * Sets ?kind=screenshot so SupportGate runs the screenshot MIME whitelist.
 */
export const uploadScreenshot = async (ticketId, blob, { maskedCount = 0 } = {}) => {
    const fd = new FormData()
    const file = new File([blob], 'screenshot.png', { type: blob.type || 'image/png' })
    fd.append('file', file)
    const qs = new URLSearchParams({ kind: 'screenshot', masked: String(maskedCount) }).toString()
    return apiFetch(`${BASE}/tickets/${ticketId}/attachments?${qs}`, {
        method: 'POST',
        body: fd,
    })
}

/**
 * Generic typed upload: video / audio / file with explicit kind.
 * PR-3 entry point for VideoRecorder + AudioRecorder.
 */
export const uploadAttachmentTyped = async (ticketId, blob, kind, { filename, mime } = {}) => {
    const fd = new FormData()
    const fname = filename || `${kind}.bin`
    const file = new File([blob], fname, { type: mime || blob.type || 'application/octet-stream' })
    fd.append('file', file)
    const qs = new URLSearchParams({ kind }).toString()
    return apiFetch(`${BASE}/tickets/${ticketId}/attachments?${qs}`, {
        method: 'POST',
        body: fd,
    })
}

// ── Large video uploads via Cloudflare Stream (browser→Stream direct, tus) ──
// The bytes go straight to Cloudflare Stream, never through our API body.
// stream-upload + complete are our API (apiFetch); the resumable tus upload
// itself is an external host (Cloudflare) via tus-js-client, so the
// check_client_timeout policy (which covers OUR API only) is satisfied.

export const streamUpload = (ticketId, { filename, mime, sizeBytes, kind }) =>
    apiFetch(`${BASE}/tickets/${ticketId}/attachments/stream-upload`, {
        method: 'POST',
        body: JSON.stringify({ filename, mime, size_bytes: sizeBytes, kind }),
    })

export const completeAttachment = (ticketId, { uid, kind, mime, sizeBytes, filename }) =>
    apiFetch(`${BASE}/tickets/${ticketId}/attachments/complete`, {
        method: 'POST',
        body: JSON.stringify({ uid, kind, mime, size_bytes: sizeBytes, filename }),
    })

/**
 * Upload the file straight to Cloudflare Stream over the resumable tus protocol,
 * using the one-time `uploadURL` the backend minted. External host — tus-js-client
 * (resumable, retrying) reports progress via its own onProgress callback.
 * `onProgress(pct)` is called 0..100. Resolves on success, rejects on error.
 */
export const uploadToStream = (uploadURL, file, { onProgress } = {}) =>
    new Promise((resolve, reject) => {
        // Lazy import keeps tus out of the main bundle until a big video is picked.
        import('tus-js-client')
            .then((tus) => {
                const upload = new tus.Upload(file, {
                    // Upload already created by the backend — tus continues into it.
                    uploadUrl: uploadURL,
                    // Chunk is a multiple of 256 KiB (Cloudflare Stream requirement);
                    // 50 MiB balances throughput and memory for big files.
                    chunkSize: 50 * 1024 * 1024,
                    retryDelays: [0, 3000, 6000, 12000],
                    metadata: { filename: file.name, filetype: file.type },
                    onProgress: (sent, total) => {
                        if (typeof onProgress === 'function') {
                            onProgress(total ? Math.round((sent / total) * 100) : 0)
                        }
                    },
                    onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                    onSuccess: () => resolve(true),
                })
                upload.start()
            })
            .catch(reject)
    })

// Path to the authed redirect endpoint (short-TTL signed HLS URL for Stream video).
export const attachmentWatchUrl = (attachmentId) =>
    `${BASE}/attachments/${attachmentId}/watch`

export const STATUS_LABELS = {
    new: 'Новый',
    triaging: 'Триаж',
    clarifying: 'Уточнение',
    in_progress: 'В работе',
    fixed: 'Исправлено',
    verified: 'Подтверждено',
    closed: 'Закрыто',
    rejected: 'Отклонено',
    duplicate: 'Дубликат',
    planned: 'В плане',
    building: 'В разработке',
    shipped: 'Выпущено',
}

// ── Производные для опыта заявителя (R-3/R-4 support-user-experience-2026q2) ──
// Линейный прогресс обращения для таймлайна. -1 = off-ramp (отклонено/дубликат).
export const SUPPORT_STAGES = ['Получено', 'В работе', 'Решено', 'Закрыто']
export const STATUS_STAGE = {
    new: 0, triaging: 0,
    clarifying: 1, in_progress: 1, planned: 1, building: 1,
    fixed: 2, shipped: 2,
    verified: 3, closed: 3,
    rejected: -1, duplicate: -1,
}

// Тон статуса (единый для inbox и detail). Резолвится в var() через TONE_COLOR.
export const STATUS_TONE = {
    new: 'info', triaging: 'warning', clarifying: 'warning',
    in_progress: 'accent', planned: 'info', building: 'accent',
    fixed: 'success', shipped: 'success', verified: 'success',
    closed: 'muted', rejected: 'danger', duplicate: 'muted',
}
export const TONE_COLOR = {
    info: 'var(--color-info)',
    warning: 'var(--color-warning)',
    accent: 'var(--color-accent)',
    success: 'var(--color-success)',
    danger: 'var(--color-danger)',
    muted: 'var(--color-text-muted)',
}

// Короткое человеческое «что это значит» — подсказка заявителю под статусом.
export const STATUS_HINT = {
    new: 'Получили обращение, скоро возьмём в работу.',
    triaging: 'Разбираемся, в чём дело.',
    clarifying: 'Нужны уточнения — посмотрите переписку ниже.',
    in_progress: 'Работаем над решением.',
    planned: 'Приняли идею в план развития.',
    building: 'Делаем — скоро выпустим.',
    fixed: 'Готово — проверьте, что всё работает.',
    shipped: 'Выпустили — попробуйте.',
    verified: 'Вы подтвердили, что помогло. Спасибо!',
    closed: 'Обращение закрыто.',
    rejected: 'Не получится исправить — см. комментарий в переписке.',
    duplicate: 'Похожее обращение уже в работе.',
}

// Терминальные статусы — обращение «решено/закрыто» (для счётчиков inbox).
export const RESOLVED_STATUSES = new Set(['fixed', 'shipped', 'verified', 'closed'])
export const OFFRAMP_STATUSES = new Set(['rejected', 'duplicate'])

export const ALLOWED_TRANSITIONS = {
    new: ['triaging', 'rejected', 'duplicate', 'closed'],
    triaging: ['clarifying', 'in_progress', 'rejected', 'duplicate', 'closed'],
    clarifying: ['triaging', 'in_progress', 'rejected', 'closed'],
    in_progress: ['clarifying', 'fixed', 'rejected', 'closed'],
    fixed: ['verified', 'in_progress', 'closed'],
    verified: ['closed', 'in_progress'],
    closed: [],
    rejected: [],
    duplicate: [],
}
